import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { AnyObject, FieldOption, FormFieldConfig, FormFieldType, ValueType } from '@mri/types';
import { FormDataSetComponent } from '@mri/app/modules';
import { filter, forkJoin, mergeMap, Observable, of, Subject, Subscription, tap } from 'rxjs';
import { ConfigResourceService, GenericResourceService } from '@mri/app/resources';
import { map } from 'rxjs/operators';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

type OptionSearch = FieldOption & { key: string, valueReal?: string, fieldLabel: string };

const optionTypes = [FormFieldType.OPTIONS, FormFieldType.OPTIONS_MULTIPLE];

@Component({
  selector: 'app-filter-search-data-set',
  templateUrl: './filter-search-data-set.component.html',
  styleUrl: './filter-search-data-set.component.scss',

})
export class FilterSearchDataSetComponent {

  private _sub = new Subscription();
  private _valueRecord: Record<string, ValueType | Array<ValueType>>;
  private _fields: Array<FormFieldConfig>;
  private _search$ = new Subject<string>();
  private _lastSearch: string = '';
  private _consultValue$ = new Subject<{ key: string, value: string }>();
  private _memoryOptions: Array<OptionSearch> = [];

  @ContentChild('prefixTemplate') prefixTemplate: TemplateRef<ElementRef>;

  @Output() valueRecordChange = new EventEmitter<Record<string, ValueType | Array<ValueType>>>();
  @Output() iconClick = new EventEmitter<AnyObject>();
  @Input() valueDefaultRecord!: Record<string, ValueType | Array<ValueType>>;
  @Input() hideClear = false;
  @Input() iconClass: string;
  @Input() elementCount?: number;

  @Input() set valueRecord(value: Record<string, ValueType | ValueType[]> | undefined) {
    if(!value) return;

    this.selectedOptions = [];
    this._valueRecord = value;
    this._reviewValues();
  }

  @Input() set fields(value: Array<FormFieldConfig>) {
    this._fields = value;
    this._reviewValues();
  }

  @ViewChild('formComponent') formComponent: FormDataSetComponent;
  valueSelected = new Set<string>;
  optionList: Array<OptionSearch> = [];
  selectedOptions: Array<OptionSearch> = [];
  search: string;

  constructor(
    private _genericSrv: GenericResourceService,
    private _configSrv: ConfigResourceService,
    private _cv: ChangeDetectorRef,
  ) {
    this._sub.add(
      this._consultValue$.pipe(
        mergeMap(({ key, value }) => {
          const field = this._fields.find(f => f.key === key || f.keys?.includes(key));
          if (!field) return of(undefined);

          return this._getOptionOfValueField(value, key, field);
        }),
        filter(option => !!option),
        tap(option => {
          if (!option) return;

          this._updateMemoryOptions([option]);

          for (const [index, selectedOption] of this.selectedOptions.entries()) {
            const { value } = selectedOption;

            if (value === option.value) {
              this.selectedOptions[index] = option;

              return;
            }
          }

        }),
        tap(() => this._cv.detectChanges()),
        tap(() => this._cv.markForCheck())

      )
        .subscribe()
    );

    this._sub.add(
      this._search$.pipe(
        filter(value => this._lastSearch !== value),
        tap(value => this._lastSearch = value),
        mergeMap(value => this._getSearchOptions(value)),
        tap(options => this._updateMemoryOptions(options)),
        tap(options => this.optionList = options),
        tap(() => this._cv.detectChanges()),
        tap(() => this._cv.markForCheck())
      ).subscribe()
    );

    this._sub.add(
      this.valueRecordChange.pipe(
        tap(() => this.search = ''),
        tap(() => {
          this.selectedOptions = [];
          this._reviewValues();
        }),
        tap(() => this._cv.detectChanges()),
        tap(() => this._cv.markForCheck())
      )
        .subscribe()
    )
  }

  onChangeValues(valueRecord: Record<string, ValueType | Array<ValueType>>) {
    this.valueRecord = valueRecord;
    this.valueRecordChange.emit(this.valueRecord);
  }

  clear() {
    const newValue: Record<string, undefined | []> = {};
    this.fields.forEach(field => {
      const keys = field.keys || [field.key];
      keys.forEach(key => {
        newValue[key || ''] = field.type === FormFieldType.OPTIONS_MULTIPLE ? [] : undefined;
      });

    });
    this.valueRecord = newValue as AnyObject;

    this.valueRecordChange.emit(this.valueRecord);
  }

  submit() {
    this.valueRecordChange.emit(this.formComponent.valueRecord);
  }

  onSelect(option: OptionSearch): void {
    const field = this._fields.find(f => f.key === option.key);
    if (!field) return;

    const valueRecord = this._valueRecord;

    if (field.type !== FormFieldType.OPTIONS_MULTIPLE) {
      valueRecord[option.key] = option.valueReal;
      return this.valueRecordChange.emit(this._valueRecord);
    }

    if (Array.isArray(valueRecord[option.key])) valueRecord[option.key].push(option.valueReal);
    else valueRecord[option.key] = [option.valueReal, valueRecord[option.key]].filter(v => !!v);

    return this.valueRecordChange.emit(this._valueRecord);

  }

  onKeyUp() {
    this._search$.next(this.search || '');
  }

  onFocus() {

  }

  onBlur() {

  }

  onRemove(option: OptionSearch) {
    const field = this._fields.find(f => f.key === option.key);
    if (!field) return;

    const valueRecord = this._valueRecord;

    if (field.type !== FormFieldType.OPTIONS_MULTIPLE) {
      delete valueRecord[option.key];
      return this.valueRecordChange.emit(this._valueRecord);
    }

    if (!Array.isArray(valueRecord[option.key])) {
      delete valueRecord[option.key];
      return this.valueRecordChange.emit(this._valueRecord);
    }

    if(Array.isArray(valueRecord[option.key])) {
      const value: Array<AnyObject> = valueRecord[option.key];
      const index = value.indexOf(option.valueReal);
      if (index === -1) return;

      value.splice(index, 1);
      return this.valueRecordChange.emit(this._valueRecord);

    }
    return this.valueRecordChange.emit(this._valueRecord);


  }

  private _getOptionOfValueField(value: ValueType, key: string, field: FormFieldConfig): Observable<OptionSearch | undefined> {
    const { optionsGateway, optionsUrl, optionsInvertUrl, optionsConfigKey } = field;
    const optionUrl = optionsUrl || optionsInvertUrl || (optionsGateway ? `${optionsGateway}/choice` : null);

    if (optionUrl)
      return this._genericSrv.getElements<FieldOption>(optionUrl, { filter: { value } })
        .pipe(
          map(response => response.objects[0] ? ({ ...(response.objects[0] || {}), key }) : undefined),
          map(option => option ? ({
            ...option, key,
            label: option.label,
            value: `${key}|${value}`,
            valueReal: value,
            fieldLabel: field.label || ''
          }) : undefined)
        );

    if (optionsConfigKey)
      return of(this._configSrv.getOptions(optionsConfigKey).find(f => f.value === value))
        .pipe(
          map(option => option ? ({
            ...option, key,
            label: option.label || '',
            value: `${key}|${value}`,
            valueReal: value,
            fieldLabel: field.label || ''
          }) : undefined)
        );

    return of(undefined);
  }

  private _getOptionOfSearchField(search: string, key: string, field: FormFieldConfig): Observable<OptionSearch | undefined> {
    const { optionsGateway, optionsUrl, optionsInvertUrl, optionsConfigKey } = field;
    const optionUrl = optionsUrl || optionsInvertUrl || (optionsGateway ? `${optionsGateway}/choice` : null);

    if (optionUrl)
      return this._genericSrv.getElements<FieldOption>(optionUrl, {
        filter: { search },
        paginate: { page: 1, size: 1 }
      })
        .pipe(
          map(response => response.objects[0] ? ({ ...(response.objects[0] || {}), key }) : undefined),
          map(option => option ? ({
            ...option, key,
            label: option.label,
            value: `${key}|${option.value}`,
            valueReal: option.value,
            fieldLabel: field.label || ''
          }) : undefined)
        );

    if (optionsConfigKey)
      return of(this._configSrv.getOptions(optionsConfigKey).find(o => new RegExp(search, 'gi').test(o.label)))
        .pipe(
          map(option => option ? ({
            ...option, key,
            label: option.label || '',
            value: `${key}|${option.value}`,
            valueReal: option.value,
            fieldLabel: field.label || ''
          }) : undefined)
        );

    return of(undefined);
  }


  private _reviewValues() {
    if (!this._fields) return;
    if (!this._valueRecord) return;

    Object.entries(this._valueRecord)
      .forEach(([key, value]) => {
        const values = Array.isArray(value) ? value : [value];

        values.forEach(value => {
          if (!value) return;
          const keyWValue = `${key}|${value}`;

          const selectedOption = this.selectedOptions.find(o => o.key === keyWValue);
          const field = this._fields.find(f => f.key === key || f.keys?.includes(key));

          if (selectedOption) return;
          if (!field) return;

          const label = this._getLabelOfValueField(value, key, field);

          if(this.selectedOptions.find(f => f.value === keyWValue)) return;

          this.selectedOptions.push({
            label, key, value: keyWValue, valueReal: value,
            fieldLabel: field.label || ''
          });
        });
      });
  }

  private _getLabelOfValueField(value: ValueType, key: string, field: FormFieldConfig): string {
    const { options, type } = field;
    if (!optionTypes.includes(type)) return value;

    const option = options?.find(o => o.value.toString() === value.toString());
    if (option) return option.label || '';

    const memoryOption = this._memoryOptions.find(o => o.value === `${key}|${value}`);
    if (memoryOption) return memoryOption.label || '';

    if (field.optionsUrl || field.optionsGateway || field.optionsInvertUrl) {
      this._consultValue$.next({ key, value });
      return `Cargando ...`;
    }

    return `No definido`;
  }

  private _getSearchOptions(search: string): Observable<Array<OptionSearch>> {
    if (!search) return of([]);

    const options$ = this._fields.map(field => this._getSearchOptionOfFile(search, field));

    return forkJoin(options$)
      .pipe(
        map(options => options.filter(o => !!o) as Array<OptionSearch>)
      );
  }

  private _getSearchOptionOfFile(search: string, field: FormFieldConfig): Observable<OptionSearch | undefined> {
    const { options, type, key } = field;

    const fieldLabel = field.label || '';
    if (!optionTypes.includes(type)) return of({
      label: search, fieldLabel,
      value: `${field.key}|${search}`,
      valueReal: search,
      key: key || ''
    });

    const option = options?.find(o => new RegExp(search, 'gi').test(o.label));
    if (option) return of({
      ...option, fieldLabel,
      value: `${field.key}|${option.value}`,
      valueReal: option.value,
      key: key || ''
    });

    const memoryOption = this._memoryOptions.find(o => o.key === key && new RegExp(search, 'gi').test(o.label));
    if (memoryOption) return of({
      ...memoryOption, fieldLabel,
      value: `${field.key}|${memoryOption.value}`,
      valueReal: memoryOption.valueReal,
      key: key || ''
    });

    if (field.optionsUrl || field.optionsGateway || field.optionsInvertUrl)
      return this._getOptionOfSearchField(search, key || '', field);

    return of(undefined);

  }

  private _updateMemoryOptions(options: Array<OptionSearch>) {
    options.forEach(o => {
      const index = this._memoryOptions.findIndex(m => m.value === o.value);

      if (index === -1) this._memoryOptions.push(o);
      else this._memoryOptions[index] = o;
    });
  }


  optionSelect($event: MatAutocompleteSelectedEvent) {
    const option = this.optionList.find(o => $event.option.value === o.value);
    console.log({option, $event})
    if(option) this.onSelect(option);
  }

  protected readonly Number = Number;
}
